import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce'
  }
});

export interface CollectibleWithImages {
  id: string;
  type: 'coin' | 'note';
  name: string;
  slug: string;
  description: string;
  price: number;
  youtube_url?: string | null;
  created_at: string;
  status: 'available' | 'sold' | 'unavailable';
  category_id?: string | null;
  category?: {
    id: string;
    name: string;
  } | null;
  images?: {
    id: string;
    image_url: string;
    order: number;
  }[];
}

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    // Replace special characters with spaces
    .replace(/[^\w\s-]/g, ' ')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Replace spaces with hyphens
    .replace(/\s/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Add timestamp for uniqueness
    + '-' + Date.now().toString().slice(-4);
}

export function getYouTubeVideoId(url: string | null | undefined): string | null {
  if (!url) return null;

  // Try to match youtube.com URL (including shorts)
  let match = url.match(/youtube\.com\/(watch\?v=|shorts\/)([a-zA-Z0-9_-]{11})/);
  
  // If not found, try youtu.be URL
  if (!match) {
    match = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  }

  return match[2];
}

export function validateYouTubeUrl(url: string): boolean {
  if (!url) return true;
  // Updated regex to support both regular YouTube URLs and shorts
  return /^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}$/.test(url);
}

async function ensureValidSession(): Promise<boolean> {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      console.error('Session error:', sessionError);
      return false;
    }

    if (!session) {
      console.log('No session found');
      return false;
    }

    // If session exists but is close to expiry, refresh it
    if (session.expires_at) {
      const expiresAt = new Date(session.expires_at * 1000);
      const now = new Date();
      const timeUntilExpiry = expiresAt.getTime() - now.getTime();
      
      // If less than 5 minutes until expiry or already expired, refresh the session
      if (timeUntilExpiry < 300000) {
        const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('Failed to refresh session:', refreshError);
          return false;
        }
        return !!newSession;
      }
    }

    return true;
  } catch (err) {
    console.error('Error ensuring valid session:', err);
    return false;
  }
}

export async function getCollectible(slug: string): Promise<CollectibleWithImages | null> {
  try {
    if (!slug) {
      console.error('No slug provided to getCollectible');
      return null;
    }

    const hasValidSession = await ensureValidSession();
    if (!hasValidSession) {
      console.log('No valid session available');
      // Still proceed with the request as collectibles should be publicly readable
    }

    const { data, error } = await supabase
      .from('collectibles')
      .select(`
        *,
        images:collectible_images(
          id,
          image_url,
          order
        ),
        category:categories(
          id,
          name
        )
      `)
      .eq('slug', slug)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle no rows gracefully

    if (error) {
      console.error('Database error in getCollectible:', error);
      throw error;
    }

    if (!data) {
      console.log(`No collectible found with slug: ${slug}`);
      return null;
    }

    return data;
  } catch (err) {
    console.error('Error fetching collectible:', err);
    throw err; // Re-throw to let the component handle the error
  }
}

export function subscribeToCollectible(slug: string, callback: () => void) {
  return supabase
    .channel('collectible-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'collectibles',
        filter: `slug=eq.${slug}`
      },
      callback
    )
    .subscribe();
}

export async function validatePromoCode(code: string): Promise<{
  valid: boolean;
  discount_percent: number;
  message: string;
}> {
  try {
    const hasValidSession = await ensureValidSession();
    if (!hasValidSession) {
      return {
        valid: false,
        discount_percent: 0,
        message: 'Authentication required to validate promo code'
      };
    }

    // Check if the code exists and is valid
    const { data: promoCode, error: promoError } = await supabase
      .from('promo_codes')
      .select('*')
      .eq('code', code.toUpperCase())
      .single();

    if (promoError || !promoCode) {
      return {
        valid: false,
        discount_percent: 0,
        message: 'Invalid promo code'
      };
    }

    // Check if code is active
    if (!promoCode.is_active) {
      return {
        valid: false,
        discount_percent: 0,
        message: 'This promo code is inactive'
      };
    }

    // Convert dates to UTC for consistent comparison
    const now = new Date();
    const startsAt = new Date(promoCode.starts_at);
    const expiresAt = promoCode.expires_at ? new Date(promoCode.expires_at) : null;

    // Check if code has started
    if (startsAt.getTime() > now.getTime()) {
      return {
        valid: false,
        discount_percent: 0,
        message: 'This promo code is not yet active'
      };
    }

    // Check if code has expired
    if (expiresAt && expiresAt.getTime() < now.getTime()) {
      return {
        valid: false,
        discount_percent: 0,
        message: 'This promo code has expired'
      };
    }

    // Check if maximum uses reached
    if (promoCode.max_uses !== null && promoCode.used_count >= promoCode.max_uses) {
      return {
        valid: false,
        discount_percent: 0,
        message: 'This promo code has reached its maximum uses'
      };
    }

    // Code is valid, return discount
    return {
      valid: true,
      discount_percent: promoCode.discount_percent,
      message: 'Promo code applied successfully'
    };
  } catch (err) {
    console.error('Error validating promo code:', err);
    return {
      valid: false,
      discount_percent: 0,
      message: 'Failed to validate promo code'
    };
  }
}